// Pure logic behind the "ask my boat to suggest alarms" page (task
// alarms_regenerate_ui, ADR 0160). Kept out of the component so the rules that
// matter — what is ticked by default, what may be ticked at all, and how honest
// the answer is — are testable without a DOM.
//
// The wire types mirror `@neomaris/protocol`'s `AlarmRegenerationPreviewPayload`.
// They are restated here rather than imported because `cloud/web` is a browser
// bundle that does not depend on the protocol package; the cloud's Zod schema
// (`src/core/alerts/regenerate-ingest.ts`) is the enforcement boundary either
// way.

import { conditionsClause, readConditions } from './alarmSentence';
import { formatRelative } from './deviceSeen';

export type RegenerateMode = 'hardware' | 'decisions';
export type RegenerationItemStatus = 'new' | 'differs' | 'unchanged';

export interface RegenerationItem {
  recipeId: string;
  status: RegenerationItemStatus;
  title: string;
  severity: string;
  confidence: string;
  explanation: string;
  changedKeys?: string[];
  /**
   * The modes a default update tick would ACTUALLY rewrite. Present on
   * `differs`/`unchanged`. EMPTY means ticking writes nothing — see
   * {@link isTickable}.
   */
  updatableModes?: string[];
  /**
   * The BARE recipe id of a SUPERSEDED alarm the boat already has watching the
   * SAME thing this item proposes — "you already have a Tank high / low on that
   * fuel tank". Present only on `new` items, and only where the boat proved the
   * overlap by compiling the old definition (an unpinned tank alarm does not say
   * which tank it watches).
   *
   * PRESENCE MEANS DEFAULT-UNTICKED (see {@link defaultSelection}). Ticking it
   * is still legitimate and often right — the replacement is one-directional and
   * named for the tank — but doing it by DEFAULT would silently leave the owner
   * with two alarms on one tank, which is the complaint this whole change
   * answers, inverted.
   */
  alreadyCoveredBy?: string;
  /**
   * The STORED recipe ids this item would REPLACE — the UPGRADE offer (core
   * ADR-116, cloud ADR 0177). Present only on a `new` per-instance item, and
   * always beside {@link alreadyCoveredBy}, which carries the same
   * default-unticked contract for a client that has never heard of this field.
   *
   * Ticking the item still only ADDS. The old alarm goes away only because
   * {@link buildApplyBody} also names it in `retire`, and the boat refuses that
   * unless every replacement is created in the same request.
   *
   * A client that knows BOTH fields renders the upgrade wording and suppresses
   * the overlap note — see {@link upgradeNote}. "We are keeping your old alarm"
   * and "we are removing it" in one row is worse than either alone.
   */
  supersedes?: string[];
  /** Per-item staleness fingerprint, echoed back for the items being applied. */
  contentDigest: string;
  proposedModes: string[];
  currentTitle?: string;
  /**
   * The qualifier conditions a CREATE of this item writes, such as the
   * engine-running gate on an engine alarm (TOM-309). Typed `unknown` because
   * it is wire data from a boat that may be a newer build: only
   * {@link conditionLine} reads it, through `readConditions`. Absent on every
   * item from a boat built before the field, and on an unconditional alarm.
   */
  conditions?: unknown;
}

/**
 * Whether the boat ARMED what it persisted. Optional on the wire: a boat built
 * before this field existed omits it, and absent means "unknown", never "fine".
 */
export interface RegenerationArming {
  local: 'applied' | 'failed';
  ruleCount: number;
  /** The boat's WHOLE unbound set, not just this request's — intersect first. */
  pendingRecipes: string[];
}

export interface RegenerationOutcome {
  created: string[];
  updated: string[];
  /**
   * Superseded alarms the apply RETIRED, each replaced by one in `created`.
   * Optional on the wire: a boat built before the upgrade lane omits it, and
   * absent means "nothing was removed" — which is what such a boat always
   * means, so it is safe to read as an empty list here (unlike
   * {@link RegenerationArming}, where absent means "did not say").
   */
  retired?: string[];
  skipped: { recipeId: string; reason: string }[];
  arming?: RegenerationArming;
}

export interface RegenerationPayload {
  v: 1;
  requestId: string;
  commandKey: string;
  mode: RegenerateMode;
  status: 'preview' | 'applied' | 'refused';
  generatedAt: string;
  contextSessionId: string | null;
  contextGeneratedAt: string | null;
  contextSnapshotCount: number;
  contextHasVesselProfile: boolean;
  deviceCount: number;
  blockedBy: string[];
  activeCount: number;
  capRemaining: number;
  previewDigest: string;
  /** The boat's definitions digest — echoed back on apply. */
  storeDigest: string;
  items: RegenerationItem[];
  outcome?: RegenerationOutcome;
  error?: { code: string; message: string };
}

/** The `GET .../alarms/regenerate/preview` response. */
export interface StoredRegeneration {
  requestId: string;
  status: 'preview' | 'applied' | 'refused';
  mode: RegenerateMode;
  previewDigest: string;
  storedAt: string;
  payload: RegenerationPayload;
}

/**
 * Can this item be ticked at all?
 *
 * A `new` item always can — applying it creates the alarm. A `differs` item can
 * only when `updatableModes` is non-empty, because an apply writes THRESHOLDS
 * ONLY: an alarm that drifted on its title, its severity or its mode set is
 * drift the boat deliberately preserves (an operator's rename must survive a
 * regenerate), so ticking it would write nothing.
 *
 * Offering a checkbox that does nothing is the specific dishonesty this
 * function exists to prevent. The page labels those rows instead.
 */
export function isTickable(item: RegenerationItem): boolean {
  if (item.status === 'new') return true;
  if (item.status === 'differs') return (item.updatableModes?.length ?? 0) > 0;
  return false;
}

/**
 * The default selection: every `new` item CHECKED, every `differs` item
 * UNCHECKED — except a `new` item that is ALREADY COVERED, which is unchecked
 * too.
 *
 * Adding an alarm the boat does not have is additive and safe — that is the
 * whole point of the feature, and a boat that lost its definitions wants all of
 * them back. Rewriting the thresholds of an alarm that already exists is a
 * change to something the operator may have tuned by hand, so it is never a
 * default. An untickable `differs` item is not selected either, obviously.
 *
 * The exception is the one case where "additive and safe" stops being true. A
 * boat with the old combined "Tank high / low" pinned to its fuel tank is offered
 * "Fuel tank low — Fuel tank 1" for that same tank; ticking both by default
 * hands the owner two alarms on one tank, which is precisely the mess this
 * change exists to clean up. So the boat names the overlap
 * ({@link RegenerationItem.alreadyCoveredBy}), the row says so in words, and the
 * owner decides. It is unticked, NOT hidden or untickable: taking the better
 * alarm is usually the right call, and it is theirs to make.
 */
export function defaultSelection(items: readonly RegenerationItem[]): Set<string> {
  const selected = new Set<string>();
  for (const item of items) {
    if (item.status === 'new' && !isAlreadyCovered(item) && !isUpgrade(item)) {
      selected.add(item.recipeId);
    }
  }
  return selected;
}

/**
 * Is this item covered by an alarm the boat already has? Read DEFENSIVELY — the
 * field is wire data from a boat that may be a newer build, and only a non-empty
 * string may change the default. Anything else behaves exactly as before it
 * existed.
 */
export function isAlreadyCovered(item: RegenerationItem): boolean {
  const raw: unknown = item.alreadyCoveredBy;
  return typeof raw === 'string' && raw.trim().length > 0;
}

/**
 * The note under an already-covered row, naming the alarm the owner ALREADY HAS
 * in the words they see it under.
 *
 * `titleOf` resolves a recipe id to its library title, and the fallback matters:
 * the recipe is a SUPERSEDED one, so a client whose library fetch failed — or
 * which is old enough not to know that id — must still say something true. "Your
 * boat already has an alarm watching this" is that: less useful, never wrong.
 * Printing the raw `tank_high` instead would be showing an owner an identifier
 * they have never seen on any screen.
 */
export function alreadyCoveredNote(
  item: RegenerationItem,
  titleOf: (recipeId: string) => string | undefined
): string | null {
  // An UPGRADE item carries both fields, and the upgrade wording replaces this
  // one. Suppressed here rather than at the call site so the two notes can never
  // be rendered together by a surface that forgot: "we are keeping your old
  // alarm" beside "we are removing it" is worse than either alone.
  if (isUpgrade(item)) return null;
  if (!isAlreadyCovered(item)) return null;
  const title = titleOf(item.alreadyCoveredBy!.trim())?.trim();
  return title !== undefined && title.length > 0
    ? `Already covered by your existing “${title}” alarm. Tick it to add this one as well.`
    : 'Your boat already has an alarm watching this. Tick it to add this one as well.';
}

/**
 * Is this item an UPGRADE — an addition that also RETIRES an alarm the owner
 * already has (core ADR-116)?
 *
 * Read as DEFENSIVELY as {@link isAlreadyCovered}, and for a sharper reason.
 * This predicate is what puts a recipe id into the apply's `retire` list, so a
 * malformed value must never be able to ask a boat to remove something: only an
 * array holding at least one non-empty string counts, and everything else
 * behaves exactly as this field did before it existed — a plain addition.
 */
export function isUpgrade(item: RegenerationItem): boolean {
  return supersededIds(item).length > 0;
}

/**
 * The superseded ids on an item, trimmed, de-duplicated and filtered to real
 * strings. The one reader of the raw field.
 */
export function supersededIds(item: RegenerationItem): string[] {
  const raw: unknown = item.supersedes;
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const id = entry.trim();
    if (id.length > 0 && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * The note under an UPGRADE row: this addition replaces an alarm the owner
 * already has, and their numbers come with it.
 *
 * Two promises, and both have to be on screen before the tick, because the tick
 * is the consent. The first is that something will be REMOVED — the one place in
 * this whole feature where that is true, and the page's opening promise now says
 * so precisely because of this row. The second is that the thresholds the owner
 * tuned by hand move to the alarm that replaces it; without that sentence an
 * upgrade reads as "delete my settings and start again", which is what an owner
 * would reasonably refuse.
 *
 * `titleOf` resolves the superseded id to its library title, and the fallback
 * matters for the same reason it does in {@link alreadyCoveredNote}: the recipe
 * is a superseded one, so a client whose library fetch failed must still say
 * something true rather than print `tank_high` at someone.
 *
 * More than one superseded id is not possible with today's registry, but the
 * boat states the rule over a group; the plural form is here so a future pair
 * cannot render "Replaces your existing undefined alarm".
 */
export function upgradeNote(
  item: RegenerationItem,
  titleOf: (recipeId: string) => string | undefined
): string | null {
  const ids = supersededIds(item);
  if (ids.length === 0) return null;
  const titles = ids
    .map((id) => titleOf(id)?.trim())
    .filter((t): t is string => t !== undefined && t.length > 0)
    .map((t) => `“${t}”`);
  if (titles.length !== ids.length) {
    return 'Replaces an alarm you already have — its numbers carry over. Leave it unticked to keep both.';
  }
  const named =
    titles.length === 1 ? titles[0]! : `${titles.slice(0, -1).join(', ')} and ${titles.at(-1)!}`;
  return titles.length === 1
    ? `Replaces your existing ${named} alarm — its numbers carry over. Leave it unticked to keep both.`
    : `Replaces your existing ${named} alarms — their numbers carry over. Leave it unticked to keep them.`;
}

/**
 * The gate line under a `new` row, such as `Only while the engine is running
 * above 300 rpm` (TOM-309). The wording comes from `conditionsClause`, the one
 * the configured-alarm list uses, so one gate reads the same on both pages.
 *
 * `null` when there is nothing true to say:
 *
 *   - the item carries no readable conditions, which is every item from a boat
 *     built before TOM-309; or
 *   - the item is not `new`. An update writes thresholds only and keeps the
 *     stored alarm's conditions, so the proposal's gate is not what a tick
 *     leaves in force.
 *
 * A clause this build cannot read is named, not dropped: `readConditions`
 * counts it and the line says "a condition this app can’t display yet". That
 * holds when every clause is unreadable too. `readConditions` returns
 * `undefined` for such a block, so this function counts the clauses itself
 * (TOM-309). The alarm is still gated, and a missing line would read as
 * unconditional.
 */
export function conditionLine(item: RegenerationItem): string | null {
  if (item.status !== 'new') return null;
  const read = readConditions(item.conditions);
  if (read === undefined) {
    const clauses = (item.conditions as { clauses?: unknown } | null | undefined)?.clauses;
    const count = Array.isArray(clauses) ? clauses.length : 0;
    if (count === 0) return null;
    return count === 1
      ? 'Only while a condition this app can’t display yet'
      : `Only while ${count} conditions this app can’t display yet`;
  }
  const clause = conditionsClause(read);
  if (clause.length === 0) return null;
  return clause.charAt(0).toUpperCase() + clause.slice(1);
}

export interface GroupedItems {
  /** Not on the boat at all — the additions. */
  fresh: RegenerationItem[];
  /** Present, but the recipes would build them differently. */
  differs: RegenerationItem[];
  /** Present and identical. Nothing to do; collapsed in the UI. */
  unchanged: RegenerationItem[];
}

/** Split a proposal into its three bands, preserving the boat's ordering. */
export function groupItems(items: readonly RegenerationItem[]): GroupedItems {
  const grouped: GroupedItems = { fresh: [], differs: [], unchanged: [] };
  for (const item of items) {
    if (item.status === 'new') grouped.fresh.push(item);
    else if (item.status === 'differs') grouped.differs.push(item);
    else grouped.unchanged.push(item);
  }
  return grouped;
}

export interface ApplyBody {
  action: 'apply';
  mode: RegenerateMode;
  create: string[];
  update: { recipeId: string }[];
  /**
   * Superseded alarms to RETIRE, derived from the TICKED upgrade items alone
   * (core ADR-116). Omitted entirely when there are none, so a body this page
   * builds for an ordinary selection is byte-identical to the one it built
   * before the upgrade lane existed — an old boat, which ignores the field,
   * cannot tell the difference.
   *
   * NEVER assembled from anything but ticks. The whole safety argument for
   * relaxing the no-shrink invariant is that a removal happened because the
   * operator asked for it, and this list is where that consent is turned into a
   * request.
   */
  retire?: string[];
  /** The boat's definitions digest this selection was made against. */
  expectedStoreDigest: string;
  /**
   * Only the recipes being applied. Scoping it here is what stops an unrelated
   * new suggestion — which a boat still finding devices produces constantly —
   * from refusing a selection the operator already made.
   */
  expectedItemDigests: Record<string, string>;
}

/**
 * Turn a tick set into the request body.
 *
 * Ticks are split by the item's OWN status, never by what the caller thinks:
 * a `new` recipe goes in `create`, a `differs` one in `update`. Sending a
 * `differs` recipe as a create would be refused by the boat as `not_new` — the
 * split belongs here, where the statuses are known.
 *
 * `modes` is deliberately omitted from each update tick. Omitted means "every
 * mode the proposal carries", which the boat then narrows to the ones whose
 * thresholds actually changed — the same set it already reported as
 * `updatableModes`. Sending an explicit list would let the page's view drift
 * from the boat's.
 *
 * An unselected item is simply absent, which is what "untouched" means here.
 *
 * `retire` is the one field that can REMOVE something, and it is built from the
 * TICKED upgrade items only — an untouched upgrade row asks for nothing, which
 * is why those rows arrive unticked. The boat re-derives the same list from its
 * own plan and store and refuses the whole apply if ours disagrees, so this is a
 * request, never an instruction.
 */
export function buildApplyBody(
  items: readonly RegenerationItem[],
  selected: ReadonlySet<string>,
  mode: RegenerateMode,
  storeDigest: string
): ApplyBody {
  const create: string[] = [];
  const update: { recipeId: string }[] = [];
  const retire: string[] = [];
  const expectedItemDigests: Record<string, string> = {};
  for (const item of items) {
    if (!selected.has(item.recipeId)) continue;
    if (!isTickable(item)) continue;
    if (item.status === 'new') {
      create.push(item.recipeId);
      // Only a CREATE can carry a retire: the alarm being removed is removed
      // because this one replaces it, and the boat refuses a retire whose
      // replacement is not in the same `create`.
      for (const superseded of supersededIds(item)) {
        if (!retire.includes(superseded)) retire.push(superseded);
      }
    } else if (item.status === 'differs') update.push({ recipeId: item.recipeId });
    else continue;
    // Vouch for exactly what was ticked, and nothing else.
    expectedItemDigests[item.recipeId] = item.contentDigest;
  }
  return {
    action: 'apply',
    mode,
    create,
    update,
    // Omitted when empty, so an ordinary selection produces exactly the body
    // this page sent before the upgrade lane existed.
    ...(retire.length > 0 ? { retire } : {}),
    expectedStoreDigest: storeDigest,
    expectedItemDigests,
  };
}

/** How much this proposal actually knows about THIS boat. */
export type ProvenanceQuality = 'tuned' | 'partial' | 'defaults';

export interface Provenance {
  quality: ProvenanceQuality;
  /** The headline the page must show — never hidden behind a disclosure. */
  headline: string;
  /** Supporting facts, each a complete sentence. */
  notes: string[];
}

/**
 * Describe how well-founded a proposal is, in plain English.
 *
 * THIS IS THE MOST IMPORTANT FUNCTION ON THE PAGE. A regeneration built from
 * zero harvested config snapshots and no vessel profile is a VESSEL-CLASS
 * DEFAULT set — sensible starting numbers for a boat of roughly this kind, not
 * numbers tuned to this boat's actual gear. It looks exactly like a tuned set
 * on screen: same recipes, same confident titles, same tidy list. Saying so is
 * the difference between a useful suggestion and a confident-looking guess.
 *
 * `tuned`    — harvested config AND a vessel profile: the thresholds came from
 *              what this boat's own devices report.
 * `partial`  — one of the two: better than nothing, still partly generic.
 * `defaults` — neither: every threshold is a vessel-class default.
 */
export function describeProvenance(
  payload: RegenerationPayload,
  now: number = Date.now()
): Provenance {
  const hasSnapshots = payload.contextSnapshotCount > 0;
  const hasProfile = payload.contextHasVesselProfile;
  const quality: ProvenanceQuality =
    hasSnapshots && hasProfile ? 'tuned' : hasSnapshots || hasProfile ? 'partial' : 'defaults';

  const notes: string[] = [];
  notes.push(
    payload.deviceCount === 1
      ? 'Worked out from 1 device your boat reports.'
      : `Worked out from ${payload.deviceCount} devices your boat reports.`
  );
  if (hasSnapshots) {
    notes.push(
      payload.contextSnapshotCount === 1
        ? 'Your boat read the settings off 1 of its devices, so those numbers are its own.'
        : `Your boat read the settings off ${payload.contextSnapshotCount} of its devices, so those numbers are its own.`
    );
    // WHEN it read them. "Its own numbers" from a reading taken two years ago
    // is not a description of the boat as it is now, and the operator is the
    // only one who knows whether the gear has changed since. Saying "its own"
    // without a date is the same class of overstatement the whole panel exists
    // to prevent.
    if (payload.contextGeneratedAt !== null) {
      // Phrased so it reads correctly for BOTH forms `formatRelative` returns —
      // a relative phrase inside 30 days, a plain date beyond it.
      notes.push(
        `Last read from your gear: ${formatRelative(payload.contextGeneratedAt, now).toLowerCase()}. Anything fitted since is not reflected in the numbers.`
      );
    }
  } else {
    notes.push(
      'Your boat has not read the settings off any of its devices, so the numbers below are starting points, not readings from your gear.'
    );
  }
  notes.push(
    hasProfile
      ? 'It also used what you told us about this boat during setup.'
      : 'We have nothing on file about this boat from setup, so nothing here is sized to it.'
  );
  if (payload.contextSessionId === null) {
    notes.push(
      'There is no setup record on this boat to work from — these suggestions come from the equipment it can see right now.'
    );
  }
  if (payload.blockedBy.length > 0) {
    notes.push(
      `Your boat still has ${payload.blockedBy.length} unresolved network problem${
        payload.blockedBy.length === 1 ? '' : 's'
      } on file. We have suggested alarms anyway, but it is worth a look.`
    );
  }

  const headline =
    quality === 'tuned'
      ? 'These numbers came from your boat’s own equipment.'
      : quality === 'partial'
        ? 'These numbers are only partly based on your boat — treat them as a starting point.'
        : 'These are standard numbers for a boat like yours, not numbers read off your equipment.';

  return { quality, headline, notes };
}

/**
 * Did the boat actually LOOK at itself to produce this payload?
 *
 * A refusal the boat reached before it could build a plan at all (its generator
 * threw) still has to carry the required provenance fields, so it carries their
 * honest empty values: zero devices, zero snapshots, no session, no items — and
 * an EMPTY `previewDigest`, which is the documented signal (migration 0064) that
 * no plan was ever built.
 *
 * Rendering that through the provenance panel would put four fabricated
 * sentences on screen ("worked out from 0 devices", "has not read the settings
 * off any of its devices") describing a look the boat never took, and the
 * "nothing to suggest" empty card over a list it never produced. Both are the
 * exact overstatement the panel exists to prevent, so the page shows only the
 * refusal when this is false.
 */
export function hasPlan(payload: RegenerationPayload): boolean {
  return payload.previewDigest !== '';
}

/**
 * "Your old “Tank high / low” alarm was removed and its numbers moved across."
 *
 * Repeating the numbers-carry-over promise AFTER the fact is deliberate. It is
 * the reassurance the row made before the tick, and this is the moment the owner
 * would otherwise go looking for the thresholds they spent an afternoon on.
 */
function retiredSentence(
  retired: readonly string[],
  titleOf?: (recipeId: string) => string | undefined
): string {
  const titles = retired
    .map((id) => titleOf?.(id)?.trim())
    .filter((t): t is string => t !== undefined && t.length > 0)
    .map((t) => `“${t}”`);
  // All or nothing on the names: half a list reads as a bug, and the count alone
  // is already a true sentence.
  const allNamed = titles.length === retired.length;
  if (retired.length === 1) {
    return allNamed
      ? `Your old ${titles[0]!} alarm was removed and its numbers moved across.`
      : 'Your old alarm was removed and its numbers moved across.';
  }
  const named = `${titles.slice(0, -1).join(', ')} and ${titles.at(-1) ?? ''}`;
  return allNamed
    ? `Your old ${named} alarms were removed and their numbers moved across.`
    : `Your ${retired.length} old alarms were removed and their numbers moved across.`;
}

/** How the apply result must be worded. See {@link describeApplied}. */
export interface AppliedSummary {
  /** What was written. Always true — the boat committed before reporting. */
  headline: string;
  /**
   * What is actually being WATCHED, or `null` when there is nothing to qualify.
   * Never merged into the headline: writing and arming are different claims.
   */
  arming: string | null;
  /** `true` when some ticked alarm is written but not armed. */
  incomplete: boolean;
}

/**
 * Say what an apply did, separating PERSISTED from ARMED.
 *
 * The boat writes a definition and then compiles it against its live signal
 * catalog. One whose bound path is not in that catalog yet compiles `pending`
 * and evaluates nothing — it is saved, and it is not being watched. "Your boat
 * is watching them now" over such an alarm is the most damaging sentence this
 * page could print, because the operator stops looking.
 *
 * Three cases, plus the compatibility one:
 *   - every touched recipe armed        → say it is being watched;
 *   - some are pending                  → name how many, and why;
 *   - the recompile itself failed       → say nothing is armed yet;
 *   - the boat sent no `arming` at all  → claim only that they were saved.
 *
 * A REMOVAL IS ALWAYS STATED (core ADR-116). An upgrade retires the alarm it
 * replaces, and the only way an owner learns that happened is this line — the
 * old alarm simply stops being on the Alarms page otherwise, which is
 * indistinguishable from the disappearance ADR 0159 exists because of. It is a
 * SEPARATE sentence rather than arithmetic on the added count: with today's
 * registry one removal has exactly one replacement, but the boat states the
 * rule over a GROUP, and "added 2, upgraded 1" computed as `created - retired`
 * would start lying the day a group holds two.
 *
 * `titleOf` names the alarm that went away, when the caller can resolve it.
 * Optional because every other caller of this function has no library to hand
 * and the sentence is true without the name.
 */
export function describeApplied(
  outcome: RegenerationOutcome,
  titleOf?: (recipeId: string) => string | undefined
): AppliedSummary {
  const created = outcome.created.length;
  const updated = outcome.updated.length;
  const retired = outcome.retired ?? [];
  const base =
    created > 0 && updated > 0
      ? `Added ${created} alarm${created === 1 ? '' : 's'} and updated ${updated}.`
      : created > 0
        ? `Added ${created} alarm${created === 1 ? '' : 's'}.`
        : updated > 0
          ? `Updated ${updated} alarm${updated === 1 ? '' : 's'}.`
          : 'Nothing needed changing.';
  const headline = retired.length === 0 ? base : `${base} ${retiredSentence(retired, titleOf)}`;

  if (created === 0 && updated === 0 && retired.length === 0) {
    return { headline, arming: null, incomplete: false };
  }

  const arming = outcome.arming;
  if (arming === undefined) {
    // An older boat. It saved them — that much the outcome proves — but it said
    // nothing about arming, so neither do we.
    return { headline, arming: 'They are saved on your boat.', incomplete: false };
  }
  if (arming.local === 'failed') {
    return {
      headline,
      arming:
        'Your boat saved them but could not switch them on yet. They are not being watched — your boat will try again on its own.',
      incomplete: true,
    };
  }
  const touched = new Set([...outcome.created, ...outcome.updated]);
  const pending = arming.pendingRecipes.filter((r) => touched.has(r));
  if (pending.length === 0) {
    return { headline, arming: 'Your boat is watching them now.', incomplete: false };
  }
  return {
    headline,
    arming:
      pending.length === created + updated
        ? 'None of them are being watched yet — your boat is not seeing the readings they need. It will start watching as soon as those appear.'
        : `${pending.length} of them ${pending.length === 1 ? 'is' : 'are'} not being watched yet — your boat is not seeing the readings ${pending.length === 1 ? 'it needs' : 'they need'}. It will start watching as soon as those appear. The rest are being watched now.`,
    incomplete: true,
  };
}

/**
 * When the boat worked this out, in the operator's words.
 *
 * A stored proposal lives for 24 h and the page adopts whatever is there on
 * open — which may be yesterday's, or one another crew member asked for on
 * another device. Rendered with no date it is indistinguishable from an answer
 * computed five seconds ago, and an owner who has fitted gear since would act
 * on a list that cannot know about it.
 */
export function proposalAgeLine(payload: RegenerationPayload, now: number = Date.now()): string {
  return `Your boat worked this out ${formatRelative(payload.generatedAt, now).toLowerCase()}.`;
}

/**
 * `sessionStorage` key holding the request the browser is still WAITING on for
 * one boat.
 *
 * The page promises the answer will be there when you come back, and a
 * regeneration is a real boat round trip that outlives a navigation. Without
 * this the remount reads the LATEST stored proposal, which — until the boat's
 * reply lands — is the PREVIOUS one, and renders it as the answer to the
 * request just made. Per-boat and per-tab: it is a "what am I waiting for"
 * marker, not a record.
 */
export function pendingRequestKey(boatId: string): string {
  return `tombolo.regen.pending.${boatId}`;
}

/** A parsed pending marker, or `null` when there is none or it has expired. */
export interface PendingRequest {
  requestId: string;
  action: 'preview' | 'apply';
  /** Epoch ms the request was issued, so a resumed wait keeps ONE deadline. */
  startedAt: number;
}

/** Read a pending marker, dropping anything malformed or past `ttlMs`. */
export function parsePendingRequest(
  raw: string | null,
  ttlMs: number,
  now: number = Date.now()
): PendingRequest | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { requestId, action, startedAt } = parsed as Record<string, unknown>;
  if (typeof requestId !== 'string' || requestId.length === 0) return null;
  if (action !== 'preview' && action !== 'apply') return null;
  if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) return null;
  if (now - startedAt >= ttlMs) return null;
  return { requestId, action, startedAt };
}

/**
 * Plain-English reason a requested recipe was not written. The boat's codes are
 * precise and unreadable; every one is mapped here rather than shown raw,
 * because a skip the operator cannot interpret reads as a failure.
 */
export function skipReasonLabel(reason: string): string {
  switch (reason) {
    case 'no_threshold_change':
      return 'Nothing to change — the numbers already match.';
    case 'withdrawn':
      return 'This alarm is no longer offered: no boat can watch it.';
    case 'unchanged':
      return 'Already set up exactly like this.';
    case 'not_new':
      return 'Your boat already has this alarm, so it was left alone.';
    case 'no_active_definition':
      return 'Your boat does not have this alarm, so there was nothing to update.';
    case 'custom_definition':
      return 'You built this one by hand, so it was left alone.';
    case 'not_proposed':
      return 'No longer suggested for this boat.';
    case 'unknown_recipe':
      return 'Your boat does not recognise this alarm.';
    case 'duplicate_request':
      return 'Asked for twice; done once.';
    default:
      return 'Left alone.';
  }
}

/** Plain-English refusal, keyed on the boat's error code. */
export function refusalLabel(code: string): string {
  switch (code) {
    case 'regenerate_preview_stale':
      return 'Your boat’s alarms changed while this page was open, so it would not apply choices made against the old list. The fresh suggestions are below.';
    case 'alert_defs_cap_exceeded':
      return 'That would leave your boat with more alarms than it can carry. Remove a few, or pick fewer.';
    case 'alert_defs_payload_too_large':
      return 'The resulting alarm list would be too large for your boat to send us. Pick fewer.';
    case 'regenerate_failed':
      return 'Your boat could not work out a suggestion. Try again in a moment.';
    // The two upgrade-lane refusals (core ADR-116). Both mean the boat would not
    // remove an alarm on the strength of what it was sent, and both are stated
    // as a REFUSAL TO REMOVE rather than a generic error — an owner who ticked
    // "replace my old tank alarm" needs to know their old alarm is still there.
    case 'retire_not_superseded':
      return 'Your boat would not remove that alarm — its own suggestions no longer say this one replaces it. Nothing was changed; the fresh suggestions are below.';
    case 'retire_partial_upgrade':
      return 'Removing that alarm would have left part of what it watches unwatched, so your boat refused the whole thing. Nothing was changed — tick every alarm that replaces it, or leave it in place.';
    default:
      return 'Your boat declined this. Nothing was changed.';
  }
}

/**
 * Which fields drifted, in plain English. `changedKeys` are the compared
 * definition fields; the operator does not speak `notificationUrgencyByMode`.
 */
export function changedKeyLabel(key: string): string {
  switch (key) {
    case 'title':
      return 'name';
    case 'severity':
      return 'how serious it is';
    case 'enabledModes':
      return 'when it is armed';
    case 'thresholdsByMode':
      return 'the numbers';
    case 'notificationUrgencyByMode':
      return 'how you get told';
    case 'sourcePaths':
      return 'what it watches';
    case 'deviceIds':
      return 'which device it watches';
    default:
      return key;
  }
}

/**
 * Did the read fail because the cloud could not STORE the boat's reply?
 *
 * The distinction matters more than it looks. While a proposal is missing the
 * page has to choose between two messages that give opposite advice: "your boat
 * has not answered yet, try again" and "your boat answered, we could not keep
 * it, retrying will not help". Guessing produces the failure this predicate
 * exists to prevent — a cloud running without its migration told the operator
 * their boat was slow, and offered a retry that could never succeed.
 */
export function isRegenerationNotStoredError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const e = err as { status?: unknown; body?: unknown };
  if (e.status !== 503) return false;
  const body = e.body;
  if (typeof body !== 'object' || body === null) return false;
  return (body as { error?: unknown }).error === 'regeneration_not_stored';
}

/** The operator-facing detail on such a failure, when the server sent one. */
export function regenerationNotStoredDetail(err: unknown): string | undefined {
  if (!isRegenerationNotStoredError(err)) return undefined;
  const body = (err as { body?: { detail?: unknown } }).body;
  const detail = body?.detail;
  return typeof detail === 'string' && detail.length > 0 ? detail : undefined;
}
